import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  COST_GROUPS,
  type CostGroup,
  type FinanceDay,
  type FinanceSegment,
  financeApi,
} from "@/api/finance";
import { segmentColor } from "@/lib/segmentColors";
import { metricsApi } from "@/api/metrics";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import { PeriodNav } from "@/components/PeriodNav";
import { type Period, calendarRangeFor, fmt as fmtCanonical, num as numCanonical, todayIso } from "@/lib/format";

// Локальні shim'и: на цій сторінці NULL трактується як 0 (не «—»).
const num = (v: number | string | null | undefined): number => numCanonical(v) ?? 0;
const fmt = (n: number, digits = 0): string => fmtCanonical(n, digits);

export function FinancePage() {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<Period>("month");
  const [end, setEnd] = useState(todayIso());
  // Сегмент: 'all' | 'kgu' | 'solar:<uuid>' (FR-FIN-05). За замовчуванням — всі.
  const [segment, setSegment] = useState<string>("all");

  const { from, to } = useMemo(() => calendarRangeFor(end, period), [end, period]);

  const daily = useQuery({
    queryKey: ["metrics-daily", from, to],
    queryFn: () => metricsApi.daily(from, to),
  });

  const fin = useQuery({
    queryKey: ["finance-summary", from, to],
    queryFn: () => financeApi.summary(from, to),
  });

  const finDaily = useQuery({
    queryKey: ["finance-daily", from, to],
    queryFn: () => financeApi.daily(from, to),
  });

  const segments = fin.data?.segments ?? [];
  const shown: FinanceSegment[] =
    segment === "all" ? segments : segments.filter((s) => s.key === segment);
  const isKguView = segment === "all" || segment === "kgu";

  const finTotals = useMemo(() => {
    let revenue = 0;
    let cogs = 0;
    let sga = 0;
    let ebitda = 0;
    for (const s of shown) {
      revenue += num(s.revenue_uah);
      cogs += num(s.cogs_uah);
      sga += num(s.sga_uah);
      ebitda += num(s.ebitda_uah);
    }
    return { revenue, cogs, gm: revenue - cogs, sga, ebitda };
  }, [shown]);

  const totals = useMemo(() => {
    const rows = daily.data ?? [];
    let revenue = 0;
    let cost = 0;
    let profit = 0;
    let imbalance = 0;
    let heat = 0;
    for (const r of rows) {
      revenue += num(r.fact_revenue_uah);
      profit += num(r.fact_profit_uah);
      imbalance += num(r.imbalance_payment_uah);
      heat += num(r.fact_heat_revenue_uah);
    }
    cost = revenue - profit;
    return { revenue, cost, profit, imbalance, heat };
  }, [daily.data]);

  /** Серії графіка: КГУ + кожна станція. При вибраному сегменті лишається одна. */
  const chartSeries = useMemo(() => {
    const first = finDaily.data?.[0];
    if (!first) return [];
    let plantIdx = 0;
    return first.segments
      .filter((s) => segment === "all" || s.key === segment)
      .map((s) => ({
        key: s.key,
        name: s.kind === "kgu" ? t("finance.seg_kgu") : s.name,
        color: segmentColor(s.key, s.kind === "kgu" ? 0 : plantIdx++),
      }));
  }, [finDaily.data, segment, t]);

  /** Стовпчик за добу: прибуток до SG&A по кожному сегменту + план КГУ. */
  const chartData = useMemo(() => {
    return (finDaily.data ?? []).map((r) => {
      const row: Record<string, string | number | null> = { day: r.day.slice(5) };
      for (const s of r.segments) {
        if (segment !== "all" && s.key !== segment) continue;
        row[s.key] = num(s.profit_uah);
      }
      // План є лише у КГУ — показуємо його тільки коли КГУ у полі зору.
      row.plan_kgu = isKguView ? num(r.plan_profit_kgu_uah) : null;
      return row;
    });
  }, [finDaily.data, segment, isKguView]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{t("finance.title")}</CardTitle>
          <CardDescription>{t("finance.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent>
          <PeriodNav
            period={period}
            refDate={end}
            onPeriodChange={setPeriod}
            onRefDateChange={setEnd}
          />
          <div className="flex items-center gap-2 flex-wrap mt-3">
            <Button variant={segment === "all" ? "default" : "outline"} size="sm"
              onClick={() => setSegment("all")}>
              {t("finance.seg_all")}
            </Button>
            {segments.map((s) => (
              <Button key={s.key} variant={segment === s.key ? "default" : "outline"} size="sm"
                onClick={() => setSegment(s.key)}>
                {s.kind === "kgu" ? t("finance.seg_kgu") : s.name}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Kpi
          label={t("finance.ebitda_total")}
          value={fmt(num(fin.data?.ebitda_total_uah))}
          tone={num(fin.data?.ebitda_total_uah) >= 0 ? "positive" : "negative"}
        />
        <Kpi label={t("finance.ebitda_kgu")} value={fmt(num(fin.data?.ebitda_kgu_uah))} />
        <Kpi label={t("finance.ebitda_solar")} value={fmt(num(fin.data?.ebitda_solar_uah))} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label={t("finance.revenue")} value={fmt(finTotals.revenue)} />
        <Kpi label={t("finance.cogs")} value={fmt(finTotals.cogs)} />
        <Kpi
          label={t("finance.gross_margin")}
          value={fmt(finTotals.gm)}
          tone={finTotals.gm >= 0 ? "positive" : "negative"}
        />
        <Kpi label={t("finance.sga")} value={fmt(finTotals.sga)} />
      </div>

      {/* Небаланс і тепло існують лише у КГУ — для СЕС їх не показуємо. */}
      {isKguView && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Kpi label={t("finance.profit")} value={fmt(totals.profit)}
            tone={totals.profit >= 0 ? "positive" : "negative"} />
          <Kpi label={t("finance.imbalance")} value={fmt(totals.imbalance)} />
          <Kpi label={t("finance.heat_revenue")} value={fmt(totals.heat)} />
        </div>
      )}

      <SegmentBreakdown segments={segments} loading={fin.isLoading} />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("finance.chart_segments_title")}</CardTitle>
          <CardDescription>{t("finance.chart_segments_hint")}</CardDescription>
        </CardHeader>
        <CardContent>
          {finDaily.isLoading ? (
            <div className="text-sm text-slate-500">{t("common.loading")}</div>
          ) : chartData.length === 0 ? (
            <div className="text-sm text-slate-500">{t("home1.no_data")}</div>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                {/* stackOffset="sign": відʼємні сегменти рисуються ВНИЗ від
                    нуля, додатні — ВГОРУ від нуля. Без цього Recharts складає
                    значення кумулятивно і збитковий КГУ зсуває базу всього
                    стовпчика: прибуткова СЕС опинялась би частково під нулем. */}
                <BarChart data={chartData} stackOffset="sign">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis tickFormatter={(v) => fmt(v as number)} />
                  <Tooltip formatter={(v) => fmt(v as number)} />
                  <Legend />
                  {isKguView && (
                    <Bar dataKey="plan_kgu" name={t("finance.plan_profit_kgu")} fill="#94a3b8" />
                  )}
                  {chartSeries.map((s) => (
                    <Bar key={s.key} dataKey={s.key} stackId="fact" name={s.name} fill={s.color} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <CostFilterChart
        days={finDaily.data ?? []}
        loading={finDaily.isLoading}
        series={chartSeries}
        segment={segment}
      />
    </div>
  );
}

/** Той самий графік, але з перемикачами статей витрат (FR-FIN-08).
 *  Дозволяє побачити, наприклад, маржу «тільки газ» — знявши оренду,
 *  обслуговування та SG&A. Прибуток = виручка − сума УВІМКНЕНИХ статей. */
function CostFilterChart({
  days,
  loading,
  series,
  segment,
}: {
  days: FinanceDay[];
  loading: boolean;
  series: { key: string; name: string; color: string }[];
  segment: string;
}) {
  const { t } = useTranslation();
  // За замовчуванням — всі прямі статті увімкнені, SG&A вимкнена: так графік
  // збігається з верхнім (прибуток до SG&A).
  const [enabled, setEnabled] = useState<Record<CostGroup, boolean>>(() =>
    COST_GROUPS.reduce(
      (acc, g) => ({ ...acc, [g]: g !== "sga" }),
      {} as Record<CostGroup, boolean>,
    ),
  );

  const toggle = (g: CostGroup) =>
    setEnabled((s) => ({ ...s, [g]: !s[g] }));

  /** Які статті реально зустрічаються в даних — решту кнопок не показуємо. */
  const presentGroups = useMemo(() => {
    const found = new Set<string>();
    for (const d of days) {
      for (const s of d.segments) {
        for (const [code, v] of Object.entries(s.costs)) {
          if (num(v) !== 0) found.add(code);
        }
        if (num(s.sga_uah) !== 0) found.add("sga");
      }
    }
    return COST_GROUPS.filter((g) => found.has(g));
  }, [days]);

  const data = useMemo(
    () =>
      days.map((d) => {
        const row: Record<string, string | number> = { day: d.day.slice(5) };
        for (const s of d.segments) {
          if (segment !== "all" && s.key !== segment) continue;
          let cost = 0;
          for (const [code, v] of Object.entries(s.costs)) {
            if (enabled[code as CostGroup]) cost += num(v);
          }
          if (enabled.sga) cost += num(s.sga_uah);
          row[s.key] = num(s.revenue_uah) - cost;
        }
        return row;
      }),
    [days, enabled, segment],
  );

  const offCount = COST_GROUPS.filter(
    (g) => presentGroups.includes(g) && !enabled[g],
  ).length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t("finance.chart_filter_title")}</CardTitle>
        <CardDescription>{t("finance.chart_filter_hint")}</CardDescription>
        <div className="flex items-center gap-2 flex-wrap pt-2">
          {presentGroups.map((g) => (
            <Button
              key={g}
              size="sm"
              variant={enabled[g] ? "default" : "outline"}
              onClick={() => toggle(g)}
              title={t("finance.cost_toggle_hint")}
            >
              {enabled[g] ? "− " : ""}
              {t(`finance.cost_${g}`)}
            </Button>
          ))}
        </div>
        {offCount > 0 && (
          <div className="text-xs text-amber-600 pt-1">
            {t("finance.cost_filter_warning", { count: offCount })}
          </div>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-sm text-slate-500">{t("common.loading")}</div>
        ) : data.length === 0 ? (
          <div className="text-sm text-slate-500">{t("home1.no_data")}</div>
        ) : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} stackOffset="sign">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis tickFormatter={(v) => fmt(v as number)} />
                <Tooltip formatter={(v) => fmt(v as number)} />
                <Legend />
                {series.map((s) => (
                  <Bar key={s.key} dataKey={s.key} stackId="fact" name={s.name} fill={s.color} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/** Розшифровка ланцюжка Виручка → COGS → GM → SG&A → EBITDA по сегментах.
 *  Без неї три числа EBITDA виглядають як «магічні» — перший же запит буде
 *  «чому стільки». */
function SegmentBreakdown({
  segments,
  loading,
}: {
  segments: FinanceSegment[];
  loading: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  if (loading) return null;
  if (segments.length === 0) return null;

  const total = (pick: (s: FinanceSegment) => number): number =>
    segments.reduce((acc, s) => acc + pick(s), 0);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-base">{t("finance.breakdown_title")}</CardTitle>
          <Button variant="outline" size="sm" onClick={() => setOpen((v) => !v)}>
            {open ? t("finance.breakdown_hide") : t("finance.breakdown_show")}
          </Button>
        </div>
      </CardHeader>
      {open && (
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500">
                  <th className="p-2">{t("finance.col_metric")}</th>
                  {segments.map((s) => (
                    <th key={s.key} className="p-2 text-right">{s.name}</th>
                  ))}
                  <th className="p-2 text-right">{t("common.total")}</th>
                </tr>
              </thead>
              <tbody>
                <Line label={t("finance.revenue")} segments={segments}
                  pick={(s) => num(s.revenue_uah)} total={total((s) => num(s.revenue_uah))} />
                <Line label={t("finance.cogs")} segments={segments}
                  pick={(s) => num(s.cogs_uah)} total={total((s) => num(s.cogs_uah))} />
                <Line label={t("finance.gross_margin")} segments={segments} bold
                  pick={(s) => num(s.gross_margin_uah)}
                  total={total((s) => num(s.gross_margin_uah))} />
                <Line label={t("finance.sga")} segments={segments}
                  pick={(s) => num(s.sga_uah)} total={total((s) => num(s.sga_uah))} />
                <Line label={t("finance.ebitda")} segments={segments} bold
                  pick={(s) => num(s.ebitda_uah)} total={total((s) => num(s.ebitda_uah))} />
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500 mt-3">{t("finance.sga_note")}</p>
        </CardContent>
      )}
    </Card>
  );
}

function Line({
  label,
  segments,
  pick,
  total,
  bold,
}: {
  label: string;
  segments: FinanceSegment[];
  pick: (s: FinanceSegment) => number;
  total: number;
  bold?: boolean;
}) {
  const cls = bold ? "font-semibold" : "";
  return (
    <tr className={"border-t border-slate-100 " + cls}>
      <td className="p-2">{label}</td>
      {segments.map((s) => (
        <td key={s.key} className="p-2 text-right font-mono">{fmt(pick(s))}</td>
      ))}
      <td className="p-2 text-right font-mono">{fmt(total)}</td>
    </tr>
  );
}

function Kpi({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "positive" | "negative";
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-slate-500">{label}</div>
        <div
          className={
            "text-xl font-mono font-semibold " +
            (tone === "negative" ? "text-red-700" : tone === "positive" ? "text-emerald-700" : "")
          }
        >
          {value}
        </div>
      </CardContent>
    </Card>
  );
}
